<template>
  <Dialog :open="props.show" @update:open="onClose">
    <DialogContent class="sm:max-w-[500px]">
      <DialogHeader>
        <DialogTitle>Product</DialogTitle>
      </DialogHeader>
      <div class="flex flex-col gap-4">
        <div class="space-y-2">
          <LabelComponent>Name</LabelComponent>
          <Input type="text" v-model="productData.name.value" />
          <p v-if="productData.name.error" class="text-sm font-medium text-destructive">
            {{ productData.name.error }}
          </p>
        </div>
        <div class="space-y-2">
          <LabelComponent>SKU</LabelComponent>
          <Input type="text" v-model="productData.sku.value" />
          <p v-if="productData.sku.error" class="text-sm font-medium text-destructive">
            {{ productData.sku.error }}
          </p>
        </div>
        <div class="space-y-2">
          <CurrencyInput label="Amount" v-model="amount" />
        </div>
        <Button type="button" @click="onSubmit">
          <LayupSpinner v-if="isLoading" :size="4" />
          <span>Submit</span>
        </Button>
        <p v-if="productData.error" class="text-sm font-medium text-destructive text-center">
          {{ productData.error }}
        </p>
      </div>
    </DialogContent>
  </Dialog>
</template>

<script setup lang="ts">
import CurrencyInput from '@/components/common/CurrencyInput.vue';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import LabelComponent from '@/components/ui/label/LabelComponent.vue';
import { NetworkError } from '@/errors';
import { getOrderByIdWithPlansPaymentsAndConnectors } from '@/services/orders';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import { ref, watch } from 'vue';
import type { ModelProductResponse } from '@layup/api-sdk';
import type { Product } from '../../../../models/orders';
import { useOrderStore } from '../../../../stores/orders';
import { AddOrderProduct, UpdateOrderProduct } from '../../../../utils/order.utils';
import LayupSpinner from '../../../common/LayupSpinner.vue';

const props = defineProps<{
  show: boolean;
  selectedProduct?: ModelProductResponse;
  orderId: string;
}>();

const emit = defineEmits(['update:show']);

const orderStore = useOrderStore();
const isLoading = ref<boolean>(false);
const amount = ref<number>(0);
const productData = ref({
  name: { value: '', error: '' },
  sku: { value: '', error: '' },
  amount: { value: 0, error: '' },
  error: '',
});

const onClose = () => {
  emit('update:show');
};

watch(
  () => props.show,
  (open) => {
    if (open) {
      productData.value.name.value = '';
      productData.value.sku.value = '';
      amount.value = 0;
      productData.value.name.error = '';
      productData.value.sku.error = '';
      productData.value.amount.error = '';
      productData.value.error = '';
      if (props.selectedProduct) {
        productData.value.name.value = props.selectedProduct.name || '';
        productData.value.sku.value = props.selectedProduct.sku || '';
        amount.value = props.selectedProduct.amount || 0;
      }
    } else {
      onClose();
    }
  },
);

const onSubmit = async () => {
  validateName();
  validateSku();

  if (!productData.value.name.error && !productData.value.sku.error) {
    try {
      isLoading.value = true;
      const product: Product = {
        _id: props.selectedProduct ? props.selectedProduct._id : undefined,
        name: productData.value.name.value,
        sku: productData.value.sku.value,
        amount: amount.value || 0,
      };

      if (props.selectedProduct) {
        await UpdateOrderProduct(props.orderId, product);
      } else {
        await AddOrderProduct(props.orderId, product);
      }

      const updatedOrder = await getOrderByIdWithPlansPaymentsAndConnectors(props.orderId);
      orderStore.updateOrder(updatedOrder);
      emit('update:show');
      addMetric('product-modal', { status: 'success', channel: 'merchant' });
    } catch (error) {
      if (error instanceof NetworkError) {
        createWarning(error);
        addMetric('product-modal', { status: 'network_error', channel: 'merchant' });
      } else {
        createException(error);
        addMetric('product-modal', { status: 'error', channel: 'merchant' });
      }
      productData.value.error = `Something went wrong, failed to save product.`;
    } finally {
      isLoading.value = false;
    }
  }
};

const validateName = () => {
  if (!productData.value.name.value) {
    productData.value.name.error = 'Name is required';
  } else {
    productData.value.name.error = '';
  }
};

const validateSku = () => {
  if (!productData.value.sku.value) {
    productData.value.sku.error = 'SKU is required';
  } else {
    productData.value.sku.error = '';
  }
};
</script>
